/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, Grid, HFlow, Icon, Theme, useTheme } from 'bold-ui'
import { FormDebouncedValueSpy, SwitchField, TextField } from 'components/form'
import { PopperButton, PopperControls } from 'components/popper'
import { FormState } from 'final-form'
import createDecorator from 'final-form-calculate'
import { useFilterCounter } from 'hooks/useFilterCounter'
import { Fragment, useCallback } from 'react'
import { Form } from 'react-final-form'
import { metaPath } from 'util/metaPath'

import { listaRegulacaoTableFilterCalculator } from '../calculator-regulacao'
import {
  LISTA_REGULACAO_DEFAULT_FILTERS,
  ListaRegulacaoFilterModel,
  ListaRegulacaoTableFilterModel,
  ListaRegulacaoTableFilterPopperModel,
} from '../model-regulacao'
import { isFilterTableEqualDefault } from '../utils-regulacao'
import { ListaRegulacaoFilterPopperForm } from './ListaRegulacaoFilterPopperForm'
import { ListaRegulacaoFilterTags } from './ListaRegulacaoFilterTags'
import { ListaRegulacaoSortDropdown } from './ListaRegulacaoSortDropdown'

interface ListaRegulacaoTableFilterProps {
  filter: ListaRegulacaoFilterModel
  updateFilter(filter: ListaRegulacaoFilterModel): void
}

const path = metaPath<ListaRegulacaoTableFilterModel>()

const decorator = createDecorator(...listaRegulacaoTableFilterCalculator)

export function ListaRegulacaoTableFilter(props: Readonly<ListaRegulacaoTableFilterProps>) {
  const { filter, updateFilter } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const popperFilterCount = useFilterCounter<ListaRegulacaoTableFilterPopperModel>({
    periodo: filter.tableFilter.periodo,
    solicitantes: filter.tableFilter.solicitantes,
  })

  const isFilterDefault = isFilterTableEqualDefault(filter.tableFilter)

  const updateTableFilter = useCallback(
    (tableFilter: ListaRegulacaoTableFilterModel) => {
      updateFilter({ ...filter, tableFilter: { ...filter.tableFilter, ...tableFilter } })
    },
    [filter, updateFilter]
  )

  const handlePopperSubmit = useCallback(
    (values: ListaRegulacaoTableFilterPopperModel) => {
      updateTableFilter({ ...filter.tableFilter, ...values })
    },
    [filter, updateTableFilter]
  )

  const handleDebounceChange = (formState: FormState<ListaRegulacaoTableFilterModel>) => {
    updateTableFilter(formState.values)
  }

  const clearFilter = useCallback(
    () =>
      updateTableFilter({
        ...filter.tableFilter,
        ordem: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.ordem,
        nomeCnsCpf: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.nomeCnsCpf,
        isSomenteMeusAtendimentos: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.isSomenteMeusAtendimentos,
        periodo: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.periodo,
        solicitantes: LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter.solicitantes,
      }),
    [filter.tableFilter, updateTableFilter]
  )

  const renderFilterForm = () => {
    return (
      <Fragment>
        <FormDebouncedValueSpy onChange={handleDebounceChange} />
        <Grid alignItems='center'>
          <Cell size={5} xl={4} style={styles.filterInlineCell1}>
            <TextField name={path.nomeCnsCpf} placeholder='Pesquise pelo nome, CNS ou CPF' icon='zoomOutline' />
          </Cell>
          <Cell size={7} xl={8} style={styles.filterInlineCell2}>
            <HFlow justifyContent='space-between'>
              <SwitchField
                name={path.isSomenteMeusAtendimentos}
                label='Ver somente minhas solicitações'
                style={styles.switch}
              />
              <ListaRegulacaoSortDropdown filter={filter} updateFilter={updateFilter} />
            </HFlow>
          </Cell>
        </Grid>
      </Fragment>
    )
  }

  const renderPopper = (ctrl: PopperControls) => (
    <ListaRegulacaoFilterPopperForm
      initialValues={filter.tableFilter}
      isFilterDefault={isFilterDefault}
      onSubmit={handlePopperSubmit}
      onClear={clearFilter}
      onClose={ctrl.close}
    />
  )

  return (
    <Grid>
      <Cell size={12}>
        <HFlow style={styles.filterInline}>
          <Form<ListaRegulacaoTableFilterModel>
            onSubmit={updateTableFilter}
            render={renderFilterForm}
            initialValues={filter.tableFilter}
            decorators={[decorator]}
          />
          <PopperButton
            kind='primary'
            size='small'
            skin='default'
            placement='bottom-end'
            renderPopper={renderPopper}
            focusOnOpen={false}
          >
            <Icon icon='adjust' style={styles.filterButtonIcon} />
            Filtros ({popperFilterCount})
          </PopperButton>
        </HFlow>
      </Cell>
      <Cell size={12}>
        <ListaRegulacaoFilterTags filtros={filter.tableFilter} />
      </Cell>
    </Grid>
  )
}

const createStyles = (theme: Theme) => ({
  filterInline: css`
    grid-template-columns: 1fr;
  `,
  filterInlineCell1: css`
    ${theme.breakpoints.down('lg')} {
      width: 33.666%;
    }
  `,
  filterInlineCell2: css`
    ${theme.breakpoints.down('lg')} {
      width: 66.333%;
    }
  `,
  filterButtonIcon: css`
    margin-right: 0.5rem;
  `,
  switch: css`
    white-space: nowrap;
  `,
})
